import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n, formatDate } from '@/lib/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, CalendarCheck, Phone, Mail, Users, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pendiente: 'bg-[hsl(43_92%_55%)] text-white',
  confirmada: 'bg-[hsl(150_50%_40%)] text-white',
  sentada: 'bg-[hsl(var(--secondary))] text-white',
  no_show: 'bg-[hsl(0_70%_50%)] text-white',
  cancelada: 'bg-[hsl(var(--muted))]',
};

export default function Reservations() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('all');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    const params = {};
    if (filter !== 'all' && filter !== 'today') params.estado = filter;
    if (filter === 'today') params.fecha = new Date().toISOString().slice(0, 10);
    const [r, tt] = await Promise.all([
      api.get('/reservations', { params }),
      api.get('/tables'),
    ]);
    setItems(r.data);
    setTables(tt.data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (rid, estado) => {
    await api.put(`/reservations/${rid}`, { estado });
    load();
  };
  const remove = async (rid) => {
    if (!confirm('Eliminar reserva?')) return;
    await api.delete(`/reservations/${rid}`);
    load();
  };

  // Memoize grouping + per-date sort: avoids recomputing on every render
  const grouped = useMemo(() => {
    const acc = {};
    items.forEach((r) => {
      if (!acc[r.fecha]) acc[r.fecha] = [];
      acc[r.fecha].push(r);
    });
    // Pre-sort each bucket by hora ascending
    Object.keys(acc).forEach((d) => {
      acc[d].sort((a, b) => (a.hora || '').localeCompare(b.hora || ''));
    });
    return acc;
  }, [items]);
  const sortedDates = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  return (
    <div className="space-y-4" data-testid="reservations-page">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <h1 className="text-2xl font-heading font-bold">{t('nav_reservations')}</h1>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px]" data-testid="reservations-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'es' ? 'Todas' : 'Todas'}</SelectItem>
              <SelectItem value="today">{t('today')}</SelectItem>
              <SelectItem value="pendiente">{lang === 'es' ? 'Pendientes' : 'Pendentes'}</SelectItem>
              <SelectItem value="confirmada">{lang === 'es' ? 'Confirmadas' : 'Confirmadas'}</SelectItem>
              <SelectItem value="sentada">{lang === 'es' ? 'Sentadas' : 'Sentadas'}</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="reservations-new-button"><Plus className="h-4 w-4 mr-1" /> {t('new_order') ? (lang === 'es' ? 'Nueva reserva' : 'Nova reserva') : 'Nueva'}</Button>
        </div>
      </div>

      {items.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <CalendarCheck className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{lang === 'es' ? 'Sin reservas. Crea la primera.' : 'Sem reservas. Crie a primeira.'}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {sortedDates.map(date => (
            <Card key={date}>
              <CardContent className="p-3">
                <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
                  {formatDate(date, lang)}
                </h2>
                <div className="space-y-2">
                  {grouped[date].map(r => (
                    <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-lg border bg-[hsl(var(--card))] hover:shadow-sm" data-testid={`reservation-row-${r.id}`}>
                      <div className="font-mono text-lg font-bold w-16 shrink-0">{r.hora}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{r.cliente_nombre}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {r.personas}</span>
                          {r.cliente_telefono && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {r.cliente_telefono}</span>}
                          {r.table_numero && <span className="inline-flex items-center gap-1">Mesa {r.table_numero}</span>}
                          {r.notas && <span className="italic">{r.notas}</span>}
                        </div>
                      </div>
                      <Badge className={STATUS_COLORS[r.estado] || ''}>{r.estado}</Badge>
                      <Select value={r.estado} onValueChange={v => updateStatus(r.id, v)}>
                        <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`reservation-status-${r.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pendiente">Pendiente</SelectItem>
                          <SelectItem value="confirmada">Confirmada</SelectItem>
                          <SelectItem value="sentada">Sentada</SelectItem>
                          <SelectItem value="no_show">No show</SelectItem>
                          <SelectItem value="cancelada">Cancelada</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(r); setOpen(true); }} className="h-8 w-8" data-testid={`reservation-edit-${r.id}`}>
                        <Plus className="h-3.5 w-3.5 rotate-45" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(r.id)} className="h-8 w-8 text-[hsl(var(--destructive))]" data-testid={`reservation-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <ReservationDialog open={open} onOpenChange={setOpen} editing={editing} tables={tables} t={t} lang={lang} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function ReservationDialog({ open, onOpenChange, editing, tables, t, lang, onSaved }) {
  const [form, setForm] = useState({
    cliente_nombre: '', cliente_telefono: '', cliente_email: '',
    fecha: new Date().toISOString().slice(0, 10), hora: '20:00',
    personas: 2, table_id: '', notas: '', origen: 'manual', estado: 'pendiente',
  });
  useEffect(() => {
    if (editing) setForm({ ...editing, table_id: editing.table_id || '' });
    else setForm({
      cliente_nombre: '', cliente_telefono: '', cliente_email: '',
      fecha: new Date().toISOString().slice(0, 10), hora: '20:00',
      personas: 2, table_id: '', notas: '', origen: 'manual', estado: 'pendiente',
    });
  }, [editing, open]);
  const save = async () => {
    const payload = { ...form, table_id: form.table_id || null };
    if (editing) await api.put(`/reservations/${editing.id}`, payload);
    else await api.post('/reservations', payload);
    toast.success('OK'); onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reservation-dialog">
        <DialogHeader><DialogTitle>{editing ? t('edit') : (lang === 'es' ? 'Nueva reserva' : 'Nova reserva')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t('customer_name')}</Label><Input value={form.cliente_nombre} onChange={e => setForm({ ...form, cliente_nombre: e.target.value })} data-testid="reservation-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('phone')}</Label><Input value={form.cliente_telefono} onChange={e => setForm({ ...form, cliente_telefono: e.target.value })} /></div>
            <div><Label>{t('email')}</Label><Input value={form.cliente_email} onChange={e => setForm({ ...form, cliente_email: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>{t('date')}</Label><Input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} data-testid="reservation-date-input" /></div>
            <div><Label>Hora</Label><Input type="time" value={form.hora} onChange={e => setForm({ ...form, hora: e.target.value })} data-testid="reservation-time-input" /></div>
            <div><Label>{lang === 'es' ? 'Personas' : 'Pessoas'}</Label><Input type="number" min={1} value={form.personas} onChange={e => setForm({ ...form, personas: Number(e.target.value) })} /></div>
          </div>
          <div><Label>{t('tables')}</Label>
            <Select value={form.table_id || 'none'} onValueChange={v => setForm({ ...form, table_id: v === 'none' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">- (sin asignar)</SelectItem>
                {tables.map(tt => <SelectItem key={tt.id} value={tt.id}>Mesa {tt.numero} ({tt.capacidad}p) {tt.zona ? `· ${tt.zona}` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>{t('notes')}</Label><Input value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} placeholder={lang === 'es' ? 'Cumpleaños, alérgenos...' : 'Aniversário, alérgenos...'} /></div>
          {editing && <div><Label>{t('status')}</Label>
            <Select value={form.estado} onValueChange={v => setForm({ ...form, estado: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="confirmada">Confirmada</SelectItem>
                <SelectItem value="sentada">Sentada</SelectItem>
                <SelectItem value="no_show">No show</SelectItem>
                <SelectItem value="cancelada">Cancelada</SelectItem>
              </SelectContent>
            </Select>
          </div>}
        </div>
        <DialogFooter><Button onClick={save} disabled={!form.cliente_nombre} data-testid="reservation-save-button">{t('save')}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
